import React from "react";
import { useHistory } from "react-router-dom";

import Table, { TableRefProps } from "src/core/dashboard/components/widgets/table/Table";
import {
  IProcessJob,
  ListProcessJobsParams,
  useProcessJobsApi
} from "src/core/process-job/api/processJobs.api";
import {
  getProcessJobColumns,
  ProcessJobAvailableTableColumn
} from "src/core/shared/components/tables/processJobQueueColumns";
import ActionButton, { IAction } from "src/library/components/Button/ActionButton";
import {
  FilterParams,
  ITerminology,
  PaginatedParams
} from "src/library/utils";

interface ProcessJobQueueProps {
  //constant filers for the table
  specificFilters: ListProcessJobsParams["filters"]["name"],
  //the drop down filters for the names
  nameHeaderFilterOptions?: Array<{
    text: string;
    value: string;
  }>
  tableName: string,
  terminology: ITerminology
  excludedColumns?: ProcessJobAvailableTableColumn[]
  //a custom prefix for the view button link
  jobLinkPrefix?: string

}

const defaultPagination: PaginatedParams<IProcessJob> = {
  page: 0,
  limit: 20,
  offset: 0,
  sort: [ [ "addedAt", "DESC" ] ]
};

const ProcessJobsQueueTable: React.ReactFCWithChildren<ProcessJobQueueProps> = props => {
  const {
    terminology, nameHeaderFilterOptions, tableName
  } = props;

  const history = useHistory();
  const TableRef = React.useRef<TableRefProps>(null);
  const [ getJobsResponse, getJobs ] = useProcessJobsApi("GET_PROCESS_JOBS");
  const [ tableData, setTableData ] = React.useState<IProcessJob[]>([]);

  React.useEffect(() => {
    if (getJobsResponse.data?.result)
      setTableData(getJobsResponse.data?.result);
  }, [ getJobsResponse.data ]);

  const onFetchData = React.useCallback(
    (params: {filters: FilterParams<IProcessJob>, pagination: PaginatedParams<IProcessJob>}) => {
      //apply the name filers from specific filers, unless we use the name option filters
      params.filters = params.filters.name ? params.filters : {
        ...params.filters,
        ...props.specificFilters
      } ;
      getJobs(params);
    },
    [ getJobs, props.specificFilters ]
  );

  const columns = React.useMemo(() => {
    return getProcessJobColumns({
      //view job page
      actionButton: info => {
        const jobLinkPrefix = props.jobLinkPrefix || "/platform/process-job-queue/";

        const viewAction: IAction = {
          title: "View",
          action: () => {
            history.push(jobLinkPrefix + info.row.original.id);
          },
          newTabPath: jobLinkPrefix + info.row.original.id
        };

        const button = (
          <ActionButton
            title="Actions"
            items={[ viewAction ]}
          />
        );

        return button;
      },
      terminology,
      nameHeaderOptions: nameHeaderFilterOptions,
      excludedColumns: props.excludedColumns

    });
  }, [
    history,
    terminology,
    nameHeaderFilterOptions,
    props.excludedColumns,
    props.jobLinkPrefix
  ]);

  return (

    <Table
      ref={TableRef}
      tableOf={tableName}
      error={getJobsResponse.error}
      loading={getJobsResponse.pending}
      data={tableData && !getJobsResponse.pending ? tableData : []}
      onFetchData={onFetchData}
      defaultPagination={defaultPagination}
      pageCount={getJobsResponse.data?.pages ? getJobsResponse.data?.pages : 0}
      rowCount={getJobsResponse.data?.count ? getJobsResponse.data?.count : 0}
      columns={columns}
      defaultVisibility={{ initialisedAt: false }}
    />

  );
};

export default ProcessJobsQueueTable;
